import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Inspection {
  id: string
  product_name: string
  inspector: string
  inspection_date: string
  result: string
  score: number
  defects_found: number
  notes: string
}

interface Defect {
  id: string
  product_name: string
  defect_type: string
  severity: string
  status: string
  reported_by: string
  assigned_to: string
  reported_date: string
}

export default function QualityPage() {
  const [inspections, setInspections] = useState<Inspection[]>([])
  const [defects, setDefects] = useState<Defect[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [inspRes, defRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/quality/inspections'),
      fetch(API_BASE + '/quality/defects'),
      fetch(API_BASE + '/quality/statistics')
    ])
    if (inspRes.ok) setInspections((await inspRes.json()).inspections || [])
    if (defRes.ok) setDefects((await defRes.json()).defects || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getResultColor = (result: string) => {
    switch (result) {
      case 'pass': return 'bg-green-100 text-green-800'
      case 'fail': return 'bg-red-100 text-red-800'
      case 'conditional': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800'
      case 'major': return 'bg-orange-100 text-orange-800'
      case 'minor': return 'bg-yellow-100 text-yellow-800'
      case 'cosmetic': return 'bg-gray-100 text-gray-600'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': case 'closed': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'open': return 'bg-yellow-100 text-yellow-800'
      case 'escalated': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600'
    if (score >= 70) return 'text-yellow-600'
    return 'text-red-600'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Quality Control</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Inspection</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Inspections</div>
              <div className="text-2xl font-bold">{statistics.total_inspections}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pass Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.pass_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Defects</div>
              <div className="text-2xl font-bold text-red-600">{statistics.open_defects}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Score</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.avg_score?.toFixed(1)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">This Month</div>
              <div className="text-2xl font-bold">{statistics.inspections_this_month}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Inspections</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inspector</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {inspections.slice(0, 12).map(insp => (
                  <tr key={insp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{insp.product_name}</div>
                      <div className="text-xs text-gray-500">{insp.inspection_date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{insp.inspector}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`font-bold ${getScoreColor(insp.score)}`}>{insp.score}</span>
                      {insp.defects_found > 0 && (
                        <span className="text-xs text-red-500 ml-2">({insp.defects_found} defects)</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getResultColor(insp.result)}`}>
                        {insp.result}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Open Defects</h3>
            <div className="space-y-3">
              {defects.filter(d => d.status !== 'resolved' && d.status !== 'closed').slice(0, 10).map(defect => (
                <div key={defect.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{defect.defect_type}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getSeverityColor(defect.severity)}`}>
                      {defect.severity}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">{defect.product_name}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{defect.assigned_to || 'Unassigned'}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(defect.status)}`}>
                      {defect.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
